import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import type { AuthRefreshHandlers, IAuthTokens } from '../classes'

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

/** Skip refresh retry for credential login / refresh itself (`/login/token` may retry). */
function isAuthPath(url: string | undefined): boolean {
  if (!url)
    return false
  const path = url.split('?')[0] ?? url
  return /\/login\/refresh\/?$/i.test(path) || /\/login\/?$/i.test(path)
}

/**
 * On 401, exchange the refresh token for a new access pair and retry once.
 * Refresh always runs against the dashboard axios client (`/login/refresh`).
 * Install on every API client that sends `auth` (dash + admin) so they share one in-flight refresh.
 */
export function installAuthRefreshInterceptor(
  clients: AxiosInstance[],
  refreshClient: AxiosInstance,
  handlers: AuthRefreshHandlers,
  setAccessToken: (token: string) => void,
): number[] {
  let refreshPromise: Promise<IAuthTokens> | null = null

  async function refreshTokens(): Promise<IAuthTokens> {
    try {
      const refreshToken = handlers.getRefreshToken()
      if (!refreshToken) {
        throw new Error('Missing refresh token')
      }

      const { data } = await refreshClient.post<IAuthTokens>('/login/refresh', {
        refresh_token: refreshToken,
      })

      if (!data?.token || !data.refresh_token) {
        throw new Error('Invalid refresh response')
      }

      setAccessToken(data.token)
      handlers.onTokensRefreshed(data)
      return data
    }
    catch (error) {
      handlers.onRefreshFailed?.()
      throw error
    }
  }

  function refreshOnce(): Promise<IAuthTokens> {
    if (!refreshPromise) {
      refreshPromise = refreshTokens().finally(() => {
        refreshPromise = null
      })
    }
    return refreshPromise
  }

  return clients.map((client) => {
    return client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const original = error.config as RetriableRequestConfig | undefined
        const status = error.response?.status

        if (!original || status !== 401 || original._retry || isAuthPath(original.url)) {
          return Promise.reject(error)
        }

        original._retry = true

        const tokens = await refreshOnce()
        if (original.headers) {
          original.headers.auth = tokens.token
        }
        return client.request(original)
      },
    )
  })
}
